import React, { useEffect, useRef, useState } from "react";
import { Box, Container, Typography, Skeleton } from "@mui/material";
import { ArrowUp2, ArrowDown2 } from "iconsax-react";

export default function AccordionShowcase({
  eyebrow,
  heading,
  description,
  items,
  reverse = false,
  disclaimer,
}) {
  const [active, setActive] = useState(0);
  const [displayIndex, setDisplayIndex] = useState(0);
  const [loadedSet, setLoadedSet] = useState(() => new Set());
  const total = items.length;
  const activeRef = useRef(0);

  const markLoaded = (src) => {
    setLoadedSet((prev) => (prev.has(src) ? prev : new Set(prev).add(src)));
  };

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((activeRef.current + 1) % total);
    }, 4500);
    return () => clearInterval(timer);
  }, [total]);

  // Once the newly-active item has actually finished loading, swap the visible layer to it.
  // Until then the previously-shown media stays on screen instead of flashing an empty box -
  // Firebase Storage serves these assets with Cache-Control: max-age=0, so a fresh <img>/<video>
  // element for the same URL still costs a full network round trip, not an instant cache hit.
  useEffect(() => {
    if (loadedSet.has(items[active]?.media)) {
      setDisplayIndex(active);
    }
  }, [active, loadedSet, items]);

  const goTo = (index) => {
    setActive((index + total) % total);
  };

  const anyLoaded = loadedSet.size > 0;

  const textColumn = (
    <Box sx={{ width: { xs: "100%", md: "45%" }, display: "flex" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mr: 2,
          flexShrink: 0,
        }}
      >
        <Box
          onClick={() => goTo(active - 1)}
          sx={{
            cursor: "pointer",
            border: "1px solid #ccc",
            borderRadius: "50%",
            width: 32,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1.5,
          }}
        >
          <ArrowUp2 size={16} />
        </Box>
        {items.map((_, index) => (
          <Box
            key={index}
            onClick={() => goTo(index)}
            sx={{
              cursor: "pointer",
              width: index === active ? 10 : 8,
              height: index === active ? 10 : 8,
              borderRadius: "50%",
              backgroundColor: index === active ? "#000" : "#ccc",
              my: 0.7,
              transition: "all 0.3s ease",
            }}
          />
        ))}
        <Box
          onClick={() => goTo(active + 1)}
          sx={{
            cursor: "pointer",
            border: "1px solid #ccc",
            borderRadius: "50%",
            width: 32,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: 1.5,
          }}
        >
          <ArrowDown2 size={16} />
        </Box>
      </Box>

      <Box sx={{ flex: 1 }}>
        {items.map((item, index) => (
          <Box
            key={index}
            onClick={() => goTo(index)}
            sx={{ cursor: "pointer", mb: index === active ? 2.5 : 1.5 }}
          >
            <Typography
              fontFamily="KIAbodyLatinbasic_Bold"
              sx={{
                fontSize: index === active ? "clamp(17px, 2vw, 20px)" : "15px",
                color: index === active ? "#000" : "#8a8a8a",
                transition: "all 0.3s ease",
              }}
            >
              {item.title}
            </Typography>
            {index === active && item.description && (
              <Typography
                fontFamily="KIAbodyLatinbasic_Regular"
                sx={{ fontSize: "14px", color: "#555", mt: 1, lineHeight: 1.7 }}
              >
                {item.description}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );

  const mediaColumn = (
    <Box
      sx={{
        width: { xs: "100%", md: "50%" },
        position: "relative",
        height: { xs: 260, sm: 360, md: 440 },
        overflow: "hidden",
        backgroundColor: "#f2f2f2",
      }}
    >
      {!anyLoaded && (
        <Skeleton
          animation="wave"
          variant="rectangular"
          width="100%"
          height="100%"
          sx={{ position: "absolute", inset: 0 }}
        />
      )}
      {items.map((item, index) => {
        const src = item.media || "";
        const isVideo = src.split("?")[0].toLowerCase().endsWith(".mp4");
        const isVisible = index === displayIndex;
        const isNearActive = Math.abs(index - active) <= 1 || index === displayIndex;
        const commonStyle = {
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: isVisible ? 1 : 0,
          transition: "opacity 0.5s ease",
        };
        return isVideo ? (
          <Box
            key={src}
            component="video"
            src={isNearActive ? src : undefined}
            autoPlay={isVisible}
            loop
            muted
            playsInline
            preload={isNearActive ? "auto" : "none"}
            onCanPlayThrough={() => markLoaded(src)}
            sx={commonStyle}
          />
        ) : (
          <Box
            key={src}
            component="img"
            src={src}
            loading={isNearActive ? "eager" : "lazy"}
            decoding="async"
            onLoad={() => markLoaded(src)}
            alt={item.title}
            sx={commonStyle}
          />
        );
      })}
    </Box>
  );

  return (
    <Box sx={{ py: { xs: 4, md: 6 }, backgroundColor: "#fff" }}>
      {(heading || description) && (
        <Container maxWidth="lg" sx={{ mb: { xs: 3, md: 4 } }}>
          {eyebrow && (
            <Typography
              color="gray"
              letterSpacing={2}
              fontFamily="KIAbodyLatinbasic_Bold"
              sx={{ fontSize: "clamp(12px, 1.6vw, 14px)" }}
            >
              {eyebrow}
            </Typography>
          )}
          {heading && (
            <Typography
              fontFamily="KIAbodyLatinbasic_Bold"
              sx={{ fontSize: "clamp(24px, 4vw, 34px)", my: 1 }}
            >
              {heading}
            </Typography>
          )}
          {description && (
            <Typography
              fontFamily="KIAbodyLatinbasic_Regular"
              color="text.secondary"
              sx={{ fontSize: "clamp(14px, 1.8vw, 16px)", maxWidth: 780 }}
            >
              {description}
            </Typography>
          )}
        </Container>
      )}

      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              md: reverse ? "row-reverse" : "row",
            },
            gap: { xs: 3, md: 5 },
            alignItems: "center",
          }}
        >
          {mediaColumn}
          {textColumn}
        </Box>
      </Container>

      {disclaimer && (
        <Container maxWidth="lg" sx={{ mt: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontFamily="KIAbodyLatinbasic_Regular"
            sx={{ display: "block", fontSize: "11px" }}
          >
            {disclaimer}
          </Typography>
        </Container>
      )}
    </Box>
  );
}
